import classNames from 'classnames';
import { useScrollLock } from 'hooks/useScrollLock';
import { useCallback, useEffect } from 'react';
import { Nullable } from 'utils/types';

type ModalProps = {
  children: React.ReactNode;
  isOpen: boolean;
  onClose: () => void;
};

type ModalOverlayProps = {
  className?: string;
  onClose: () => void;
};

type ModalBodyProps = {
  children: React.ReactNode;
  className?: string;
};

type useModalProps = {
  isScrollLockActive?: boolean;
  onClose: () => void;
};

export function ModalOverlay({
  className,
  onClose,
}: ModalOverlayProps): JSX.Element {
  return (
    <div
      data-testid="modal-overlay"
      className={classNames(
        'fixed inset-0 z-40 size-full overflow-y-auto bg-black/50',
        className,
      )}
      onClick={() => onClose()}
      role="presentation"
    />
  );
}

export function ModalBody({
  children,
  className,
}: ModalBodyProps): JSX.Element {
  return (
    <div
      // eslint-disable-next-line tailwindcss/no-arbitrary-value -- Required for modal responsiveness
      className={classNames(
        'drop-shadow-md fixed top-1/2 left-1/2 z-50 flex flex max-h-[90vh] w-full max-w-4/5 -translate-x-2/4 -translate-y-2/4 flex-col flex-nowrap items-center justify-center will-change-transform lg1:max-w-screen-lg',
        className,
      )}
    >
      {children}
    </div>
  );
}

export function useModal({
  isScrollLockActive = false,
  onClose,
}: useModalProps): void {
  useScrollLock(isScrollLockActive);

  const escFunction = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    },
    [onClose],
  );

  useEffect(() => {
    document.addEventListener('keydown', escFunction, false);
    return () => {
      document.removeEventListener('keydown', escFunction, false);
    };
  }, [escFunction]);
}

export default function Modal({
  children,
  isOpen,
  onClose,
}: ModalProps): Nullable<JSX.Element> {
  useModal({ isScrollLockActive: isOpen, onClose });

  return !isOpen ? null : (
    <div data-testid="modal">
      <ModalOverlay onClose={onClose} />
      <ModalBody>{children}</ModalBody>
    </div>
  );
}
